import {forwardRef, ForwardRefRenderFunction, Ref, useEffect, useImperativeHandle, useState} from 'react';
import {
    AccountsTableViewModel,
    useGetTableViewForStarredAccountsByPropertyIdAndVersionIdsLazyQuery,
    VersionType
} from "../../../../__generated__/generated_types";

import {AccountsTableView} from "../accounts-table-view/AccountsTableView";
import {useAccountsTableViewData} from "../accounts-table-view/hooks/useAccountsTableViewData";

interface StarredAccountsTableViewProps {
    onDataUpdate: () => void,
    budgetYear: number
}

export interface StarredAccountsTableViewRefObject {
    imperativeRefreshTableData: () => void
}

const StarredAccountsTableViewRenderer:
    ForwardRefRenderFunction<StarredAccountsTableViewRefObject, StarredAccountsTableViewProps> = (
    {onDataUpdate, budgetYear},
    ref: Ref<StarredAccountsTableViewRefObject>
) => {
    const {
        isReady, propertyId, property, year,
        starringReforcastVersionId, starringBudgetVersionId,
        finTotalReforcastVersionId, finTotalBudgetVersionId
    } = useAccountsTableViewData();
    const [data, setData] = useState<AccountsTableViewModel[] | undefined>(undefined);

    const [
        fetchTableData,
        {data: tableData, loading: tableDataLoading, error: tableDataLoadingError}
    ] = useGetTableViewForStarredAccountsByPropertyIdAndVersionIdsLazyQuery({fetchPolicy: "network-only"});

    const refreshTableData = () => {
        if (isReady && propertyId && year &&
            starringReforcastVersionId && starringBudgetVersionId &&
            finTotalReforcastVersionId && finTotalBudgetVersionId) {
            fetchTableData({
                variables: {
                    propertyId: propertyId,
                    versionIds: [starringReforcastVersionId, starringBudgetVersionId],
                    financialTotalValueArgInput: [
                        {
                            versionId: finTotalReforcastVersionId,
                            year: year,
                            type: VersionType.YearTotal
                        },
                        {
                            versionId: finTotalBudgetVersionId,
                            year: year + 1,
                            type: VersionType.Budget
                        }
                    ],
                    budgetYear: budgetYear
                }
            });
        }
    };

    useImperativeHandle(
        ref,
        () => ({
            imperativeRefreshTableData() {
                refreshTableData();
            }
        }),
        [isReady, propertyId, year,
            starringReforcastVersionId, starringBudgetVersionId,
            finTotalReforcastVersionId, finTotalBudgetVersionId],
    );

    useEffect(() => {
        refreshTableData();
    }, [isReady, propertyId, year,
        starringReforcastVersionId, starringBudgetVersionId,
        finTotalReforcastVersionId, finTotalBudgetVersionId]);

    useEffect(
        () => {
            if (tableDataLoading || tableDataLoadingError || !tableData) {
                return;
            }
            if (tableData.getTableViewForStarredAccountsByPropertyIdAndVersionIds) {
                setData(tableData.getTableViewForStarredAccountsByPropertyIdAndVersionIds);
            } else {
                setData([]);
            }
        }, [tableData, tableDataLoading, tableDataLoadingError]
    );

    return (
        <AccountsTableView
            headerText={"Starred Accounts"}
            property={property}
            tableData={data}
            onStarringUpdate={() => {
                refreshTableData();
                onDataUpdate();
            }}
            onNoteUpdate={() => {
                refreshTableData();
                onDataUpdate();
            }}
        />
    );
};

const StarredAccountsTableView = forwardRef(StarredAccountsTableViewRenderer);
export default StarredAccountsTableView;
